import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { fmtDate } from "../lib/format";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { flattenInfinitePages, getNextPageParam } from "../lib/infinite-list";

interface AuditRow {
  id: string;
  action: string;
  entity: string | null;
  entityId: string | null;
  actorId: string | null;
  data: unknown;
  createdAt: string;
}

interface AuditResponse {
  items: AuditRow[];
  total: number;
  page: number;
  pageSize: number;
}

const PAGE_SIZE = 50;

export function Audit() {
  const listQuery = useInfiniteQuery({
    queryKey: ["rbac", "audit"],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => api<AuditResponse>(`/rbac/audit?page=${pageParam}&pageSize=${PAGE_SIZE}`),
    getNextPageParam: (lastPage, allPages) =>
      getNextPageParam(
        lastPage.total,
        allPages.reduce((acc, page) => acc + page.items.length, 0),
        PAGE_SIZE,
      ),
  });

  const rows = flattenInfinitePages(listQuery.data?.pages);
  const listTotal = listQuery.data?.pages[0]?.total ?? 0;
  const hasNextPage = rows.length < listTotal && (listQuery.hasNextPage ?? false);

  const columns: Column<AuditRow>[] = [
    {
      key: "action",
      header: "Action",
      sortable: true,
      sortValue: (r) => r.action,
      render: (r) => <span className="font-mono text-xs text-brand-pink">{r.action}</span>,
    },
    {
      key: "entity",
      header: "Entity",
      sortable: true,
      sortValue: (r) => r.entity ?? "",
      exportValue: (r) =>
        r.entityId && r.entity ? `${r.entity} #${r.entityId.slice(-8)}` : (r.entity ?? ""),
      render: (r) => (
        <span>
          {r.entity ?? "—"}
          {r.entityId && (
            <span className="ml-1 font-mono text-[10px] text-muted-2">#{r.entityId.slice(-8)}</span>
          )}
        </span>
      ),
    },
    {
      key: "user",
      header: "Actor",
      hideSm: true,
      sortable: true,
      sortValue: (r) => r.actorId ?? "",
      exportValue: (r) => r.actorId ?? "",
      render: (r) => (
        <span className="font-mono text-[10px] text-muted">
          {r.actorId ? `#${r.actorId.slice(-8)}` : "—"}
        </span>
      ),
    },
    {
      key: "at",
      header: "When",
      className: "text-right whitespace-nowrap",
      sortable: true,
      sortValue: (r) => r.createdAt,
      exportValue: (r) => fmtDate(r.createdAt),
      render: (r) => <span className="text-muted">{fmtDate(r.createdAt)}</span>,
    },
  ];

  return (
    <ListPageShell>
      <div className="shrink-0">
        <h1 className="font-display text-lg font-bold">Audit log</h1>
        <p className="mt-0.5 text-xs text-muted">
          Immutable record of privileged actions{listTotal ? ` · ${listTotal.toLocaleString()} entries` : ""}.
        </p>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        loading={listQuery.isLoading}
        exportFilename="audit-log"
        infiniteScroll={{
          hasNextPage,
          isFetchingNextPage: listQuery.isFetchingNextPage,
          onLoadMore: () => void listQuery.fetchNextPage(),
        }}
        empty={{ title: "No audit entries", hint: "Actions from staff will appear here." }}
      />
    </ListPageShell>
  );
}
